import { meetIvySpotlightSrc } from '@/config/marketing-assets.config'
import {
  FeaturePromptList,
  type FeaturePrompt,
} from '@/components/marketing/feature-prompt-list'
import { MarketingSectionCopy } from '@/components/marketing/marketing-section-copy'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export function FeatureSpotlightKicker({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return <span className={cn('feature-spotlight-kicker', className)}>{children}</span>
}

export function FeatureSpotlight({
  kicker,
  title,
  lead,
  imageSrc,
  imageAlt,
  ctaLabel,
  ctaHref,
  imageSide = 'right',
  prompts,
}: {
  kicker: string
  title: string
  lead: string
  imageSrc: string
  imageAlt: string
  ctaLabel: string
  ctaHref: string
  imageSide?: 'left' | 'right'
  prompts?: FeaturePrompt[]
}) {
  const isExternal = ctaHref.startsWith('http')

  return (
    <article
      className={cn(
        'feature-spotlight',
        imageSide === 'left' ? 'feature-spotlight--image-left' : 'feature-spotlight--image-right',
        prompts?.length && 'feature-spotlight--with-prompts'
      )}
    >
      <div className="feature-spotlight__grid">
        <div className="feature-spotlight__media">
          <div className="feature-spotlight__media-inner">
            <img src={imageSrc} alt={imageAlt} loading="lazy" decoding="async" />
          </div>
          <div className="feature-spotlight__media-gradient" aria-hidden />
        </div>

        <div className="feature-spotlight__content">
          <MarketingSectionCopy
            className="feature-spotlight__copy"
            kicker={kicker}
            title={title}
            lead={lead}
          >
            {prompts?.length ? <FeaturePromptList prompts={prompts} /> : null}
            {isExternal ? (
              <Button asChild variant="cta" size="cta">
                <a href={ctaHref} className="feature-spotlight__cta">
                  {ctaLabel}
                </a>
              </Button>
            ) : (
              <Button asChild variant="cta" size="cta">
                <a href={ctaHref} className="feature-spotlight__cta">
                  {ctaLabel}
                </a>
              </Button>
            )}
          </MarketingSectionCopy>
        </div>
      </div>
    </article>
  )
}

export type FeatureSpotlightItem = {
  kicker: string
  title: string
  lead: string
  imageSlug: string
  imageAlt: string
  ctaLabel: string
  ctaHref: string
  imageSide?: 'left' | 'right'
  prompts?: FeaturePrompt[]
}

export function featureSpotlightImageSrc(slug: string): string {
  return meetIvySpotlightSrc(slug)
}
